import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  Search,
  Database,
  Cloud,
  Server,
  MessageSquare,
  Mail,
  Users,
  Building2,
  Share2,
  Phone,
  Globe,
  Sparkles,
  KeyRound,
  Activity,
  CreditCard,
  Check,
  LayoutGrid,
  ChevronRight,
  Plug,
} from 'lucide-react';
import {
  CONNECTION_CATEGORIES,
  CONNECTORS,
  searchConnectors,
  type ConnectionCategoryId,
  type ConnectorDefinition,
} from '../catalog';

interface ConnectorTypeSelectorProps {
  onClose: () => void;
  onSelect: (connectorKey: string) => void;
  currentSelection?: string;
  /** When set, pre-filter to this category */
  initialCategory?: ConnectionCategoryId | null;
}

const CATEGORY_ICONS: Record<ConnectionCategoryId, React.ReactNode> = {
  databases: <Database className="w-4 h-4" />,
  cloud_storage: <Cloud className="w-4 h-4" />,
  file_transfer: <Server className="w-4 h-4" />,
  messaging: <MessageSquare className="w-4 h-4" />,
  email: <Mail className="w-4 h-4" />,
  collaboration: <Users className="w-4 h-4" />,
  crm_erp: <Building2 className="w-4 h-4" />,
  social: <Share2 className="w-4 h-4" />,
  voice_telephony: <Phone className="w-4 h-4" />,
  apis: <Globe className="w-4 h-4" />,
  ai_services: <Sparkles className="w-4 h-4" />,
  identity: <KeyRound className="w-4 h-4" />,
  observability: <Activity className="w-4 h-4" />,
  payments: <CreditCard className="w-4 h-4" />,
};

const CAPABILITY_LABELS: Record<string, string> = {
  ingest: 'Ingest',
  channel: 'Channel',
  tool: 'Tool',
  storage: 'Storage',
};

const ConnectorTypeSelector: React.FC<ConnectorTypeSelectorProps> = ({
  onClose,
  onSelect,
  currentSelection,
  initialCategory = null,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ConnectionCategoryId | null>(
    initialCategory
  );
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => searchRef.current?.focus(), 50);
    return () => window.clearTimeout(timer);
  }, []);

  const categoriesWithItems = useMemo(() => {
    const present = new Set(CONNECTORS.map((c) => c.category));
    return CONNECTION_CATEGORIES.filter((c) => present.has(c.id));
  }, []);

  const isSearching = searchQuery.trim().length > 0;

  const filtered = useMemo(
    () =>
      searchConnectors(searchQuery, {
        categoryId: isSearching ? null : selectedCategory,
        includeComingSoon: true,
      }),
    [searchQuery, selectedCategory, isSearching]
  );

  const grouped = useMemo(() => {
    const map = new Map<ConnectionCategoryId, ConnectorDefinition[]>();
    for (const connector of filtered) {
      const list = map.get(connector.category) || [];
      list.push(connector);
      map.set(connector.category, list);
    }
    return CONNECTION_CATEGORIES.filter((c) => map.has(c.id)).map((c) => ({
      ...c,
      connectors: map.get(c.id) || [],
    }));
  }, [filtered]);

  const activeCategoryMeta = selectedCategory
    ? CONNECTION_CATEGORIES.find((c) => c.id === selectedCategory)
    : null;

  const handleSelect = (connector: ConnectorDefinition) => {
    onSelect(connector.connectorKey);
  };

  const panelTitle = isSearching
    ? 'Search results'
    : activeCategoryMeta
      ? activeCategoryMeta.label
      : 'All connectors';

  const panelSubtitle = isSearching
    ? 'Matching providers across categories'
    : activeCategoryMeta?.description ?? 'Choose a provider to configure';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 bg-black/75 backdrop-blur-sm">
      <div
        className="relative flex w-full max-w-5xl h-[min(720px,90vh)] flex-col overflow-hidden rounded-2xl border border-neutral-700/80 bg-neutral-950 shadow-2xl shadow-black/50 ring-1 ring-white/5"
        role="dialog"
        aria-modal="true"
        aria-labelledby="connector-picker-title"
      >
        <header className="flex-shrink-0 border-b border-neutral-800 bg-neutral-950 px-5 py-4 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-400 ring-1 ring-indigo-500/25">
                <Plug className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 id="connector-picker-title" className="text-lg font-semibold text-white tracking-tight">
                    Add connection
                  </h2>
                  <span className="inline-flex items-center rounded-full border border-neutral-700 bg-neutral-900 px-2 py-0.5 text-[11px] font-medium tabular-nums text-neutral-300">
                    {CONNECTORS.length} providers
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-neutral-400 truncate">
                  Browse by category or search providers for your AI control panel
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="relative mt-4">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search Slack, SFTP, Salesforce, SMTP…"
              className="w-full rounded-xl border border-neutral-800 bg-neutral-900 py-2.5 pl-10 pr-10 text-sm text-white placeholder:text-neutral-500 outline-none transition focus:border-indigo-500/60 focus:ring-2 focus:ring-indigo-500/20"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-neutral-500 hover:text-white"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <aside className="hidden md:flex w-56 flex-shrink-0 flex-col border-r border-neutral-800 bg-neutral-900">
            <div className="px-3 py-3">
              <p className="px-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
                Categories
              </p>
            </div>
            <nav className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory(null);
                  setSearchQuery('');
                }}
                className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                  !selectedCategory && !isSearching
                    ? 'bg-indigo-600 text-white'
                    : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
                }`}
              >
                <LayoutGrid className="h-4 w-4 flex-shrink-0 opacity-80" />
                <span className="flex-1 font-medium truncate">All</span>
              </button>

              {categoriesWithItems.map((category) => {
                const active = selectedCategory === category.id && !isSearching;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(category.id);
                      setSearchQuery('');
                    }}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                      active
                        ? 'bg-indigo-600 text-white'
                        : 'text-neutral-300 hover:bg-neutral-800 hover:text-white'
                    }`}
                  >
                    <span className={`flex-shrink-0 ${active ? 'text-white' : 'text-neutral-500'}`}>
                      {CATEGORY_ICONS[category.id]}
                    </span>
                    <span className="flex-1 font-medium truncate">{category.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          <div className="md:hidden flex-shrink-0 border-b border-neutral-800 px-3 py-2.5 overflow-x-auto">
            <div className="flex gap-1.5 min-w-max">
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory(null);
                  setSearchQuery('');
                }}
                className={`rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap transition ${
                  !selectedCategory && !isSearching
                    ? 'bg-indigo-600 text-white'
                    : 'bg-neutral-800 text-neutral-300'
                }`}
              >
                All
              </button>
              {categoriesWithItems.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setSearchQuery('');
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap transition ${
                    selectedCategory === category.id && !isSearching
                      ? 'bg-indigo-600 text-white'
                      : 'bg-neutral-800 text-neutral-300'
                  }`}
                >
                  {CATEGORY_ICONS[category.id]}
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          <section className="flex min-h-0 min-w-0 flex-1 flex-col bg-neutral-950">
            <div className="flex-shrink-0 border-b border-neutral-800 px-5 py-3.5 sm:px-6">
              <div className="flex items-center gap-2 text-neutral-500 text-xs mb-1">
                {!isSearching && selectedCategory && (
                  <>
                    <span>Categories</span>
                    <ChevronRight className="h-3 w-3" />
                  </>
                )}
                <span className="text-neutral-300 font-medium">{panelTitle}</span>
              </div>
              <p className="text-xs text-neutral-500 leading-relaxed">{panelSubtitle}</p>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5 sm:py-5">
              {grouped.length === 0 ? (
                <div className="flex h-full min-h-[200px] flex-col items-center justify-center text-center px-6">
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-neutral-900 ring-1 ring-neutral-800">
                    <Search className="h-5 w-5 text-neutral-600" />
                  </div>
                  <p className="text-sm font-medium text-neutral-300">No connectors found</p>
                  <p className="mt-1 text-xs text-neutral-500 max-w-xs">
                    Try a different search term or pick another category from the sidebar.
                  </p>
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="mt-4 text-xs font-medium text-indigo-400 hover:text-indigo-300"
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-7">
                  {grouped.map((category) => (
                    <div key={category.id}>
                      {(isSearching || !selectedCategory) && (
                        <div className="mb-3 flex items-center gap-2">
                          <span className="text-indigo-400">{CATEGORY_ICONS[category.id]}</span>
                          <h3 className="text-xs font-semibold uppercase tracking-wide text-neutral-400">
                            {category.label}
                          </h3>
                          <div className="h-px flex-1 bg-neutral-800" />
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        {category.connectors.map((connector) => {
                          const isSelected = currentSelection === connector.connectorKey;
                          return (
                            <button
                              key={connector.connectorKey}
                              type="button"
                              onClick={() => handleSelect(connector)}
                              className={`group relative flex flex-col rounded-xl border p-4 text-left transition-all duration-150 ${
                                isSelected
                                  ? 'border-indigo-500 bg-indigo-950/40 ring-1 ring-indigo-500/40'
                                  : 'border-neutral-700 bg-neutral-800 hover:border-indigo-500/60 hover:bg-[#2a2a2a] hover:-translate-y-0.5 hover:shadow-md hover:shadow-black/30'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-900 text-indigo-400 border border-neutral-700 group-hover:border-indigo-500/40 group-hover:text-indigo-300">
                                  {CATEGORY_ICONS[connector.category]}
                                </div>
                                {isSelected ? (
                                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500 text-white">
                                    <Check className="h-3 w-3" strokeWidth={3} />
                                  </span>
                                ) : null}
                              </div>

                              <h4 className="mt-3 text-sm font-semibold leading-snug text-white group-hover:text-indigo-200">
                                {connector.displayName}
                              </h4>
                              <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-neutral-400">
                                {connector.description}
                              </p>

                              {connector.capabilities.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-1.5">
                                  {connector.capabilities.slice(0, 3).map((cap) => (
                                    <span
                                      key={cap}
                                      className="rounded-md border border-neutral-700 bg-neutral-900 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-neutral-400"
                                    >
                                      {CAPABILITY_LABELS[cap] || cap}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        <footer className="flex flex-shrink-0 items-center justify-between gap-3 border-t border-neutral-800 bg-neutral-950 px-5 py-3 sm:px-6">
          <p className="text-[11px] text-neutral-500 hidden sm:block">
            Select a provider to configure credentials and environment.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-lg border border-neutral-700 bg-neutral-900 px-4 py-2 text-sm font-medium text-neutral-300 transition hover:border-neutral-600 hover:bg-neutral-800 hover:text-white"
          >
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ConnectorTypeSelector;
